import React from 'react'
import styled from 'styled-components'

import {
    invoiceGeneratorActioned,
    BigformActionedAction,
    InvoiceGeneratorActionedField,
} from '@/analytics/segment'
import Button from '@/components/button'
import Input from '@/components/input'
import {VisuallyHidden} from '@/components/visually-hidden'
import deleteIcon from '@/images/icons/delete.svg'
import {useGlobalStrings} from '@/providers/global-strings'
import {useLocale} from '@/providers/locale'
import {focusStyles} from '@/styles/utils'
import {colors, tokens} from '@/theme'
import currencyFormatter from '@/utils/currency-formatter'
import mq from '@/utils/media-query'

import type {Dispatch, State, StateKey} from './state'
import type {InvoiceGeneratorFragment} from '@/graphql-types'
import type {ClearError, Errors, SetError} from '@/hooks/use-form-errors'

import {SectionWrapper} from './shared-styles'
import {Actions} from './state'

interface Props {
    title: string
    dispatch: Dispatch
    state: State
    content: InvoiceGeneratorFragment['data']
    errors: Errors<State>
    setError: SetError<State>
    clearError: ClearError<State>
}

export const InvoiceItems = ({
    title,
    content,
    dispatch,
    state,
    errors,
    setError,
    clearError,
}: Props) => {
    const currentLocale = useLocale()
    const globalStrings = useGlobalStrings()
    const formatCurrency = currencyFormatter(
        currentLocale.code,
        state.currency,
        {
            maximumFractionDigits: 2,
        },
        true,
    )

    React.useEffect(() => {
        const subtotal = state.invoiceItems.reduce((acc, cur) => {
            if (cur.price) {
                return acc + cur.price * cur.quantity
            }

            return acc
        }, 0)

        dispatch({
            type: Actions.SET_VALUES,
            key: 'subtotal',
            value: subtotal,
        })
    }, [state.invoiceItems, dispatch])

    const handleInputChange = (key: StateKey, value: string, item: any) => {
        dispatch({
            type: Actions.INVOICE_ITEM_UPDATED,
            id: item.id,
            key,
            value,
        })

        invoiceGeneratorActioned({
            action: BigformActionedAction.Interacted,
            field: InvoiceGeneratorActionedField.InvoiceItems,
        })

        if (!value) {
            setError(`item-name-${item.id}` as any, globalStrings.this_field_is_required)
            return
        }
        clearError(`item-name-${item.id}` as any)
    }

    return (
        <SectionWrapper title={title}>
            <InvoiceItemsGrid>
                {state.invoiceItems.map((item: any, index: number) => (
                    <React.Fragment key={item.id}>
                        <StyledInput
                            label={content.items_label}
                            value={item.name}
                            type="text"
                            error={errors[`item-name-${item.id}` as keyof Errors<State>]}
                            onChange={(value) =>
                                handleInputChange('name' as keyof Errors<State>, value, item)
                            }
                        />
                        <StyledInput
                            label={content.quantity_label}
                            value={item.quantity}
                            type="number"
                            onChange={(value) => {
                                dispatch({
                                    type: Actions.INVOICE_ITEM_UPDATED,
                                    id: item.id,
                                    key: 'quantity',
                                    value,
                                })
                            }}
                        />
                        <StyledInput
                            label={content.price_label}
                            value={item.price}
                            type="number"
                            onChange={(value) => {
                                dispatch({
                                    type: Actions.INVOICE_ITEM_UPDATED,
                                    id: item.id,
                                    key: 'price',
                                    value,
                                })
                            }}
                        />
                        <ReadOnlyInput
                            label={content.total_label}
                            value={formatCurrency(item.price * item.quantity)}
                            readOnly
                        />

                        {index > 0 ? (
                            <DeleteButton>
                                <VisuallyHidden
                                    as="input"
                                    type="button"
                                    value="delete"
                                    onClick={() => {
                                        dispatch({
                                            type: Actions.INVOICE_ITEM_DELETED,
                                            id: item.id,
                                        })
                                    }}
                                />

                                <img src={deleteIcon} alt="" />
                            </DeleteButton>
                        ) : (
                            <div />
                        )}
                    </React.Fragment>
                ))}
            </InvoiceItemsGrid>

            <StyledButton
                type="button"
                onClick={() => {
                    dispatch({
                        type: Actions.INVOICE_ITEM_ADDED,
                        id: Math.random().toString(16).substring(2),
                    })
                }}
            >
                {content.add_item_label}
            </StyledButton>
        </SectionWrapper>
    )
}

const InvoiceItemsGrid = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('md')} {
        grid-template-columns: 1fr 0.5fr 0.5fr 0.5fr ${tokens.spacing24};
    }
`

const StyledInput = styled(Input)`
    background-color: ${colors.background.default};
`

const ReadOnlyInput = styled(Input)`
    border-color: ${colors.border.secondary};
`

const DeleteButton = styled.label`
    justify-self: flex-end;
    align-self: center;

    input:focus ~ img {
        ${focusStyles()}
    }

    ${mq.min('md')} {
        margin-top: ${tokens.spacing32};
    }
`

const StyledButton = styled(Button)`
    margin-top: ${tokens.spacing40};

    ${mq.max('md')} {
        width: 100%;
    }
`
